import Image from "next/image";
import Link from "next/link";
import type { ReferencePeptide } from "@/lib/research/types";

interface HeroCompoundViewProps {
	peptide: ReferencePeptide;
	imageSrc: string | null;
	href?: string;
	featuredLabel?: string;
	sequenceLabel?: string;
	originLabel?: string;
}

function Corner({ className = "" }: { className?: string }) {
	return (
		<svg
			aria-hidden
			viewBox="0 0 14 14"
			className={`text-primary/50 absolute h-4 w-4 ${className}`}
			fill="none"
			stroke="currentColor"
			strokeWidth="1"
		>
			<path d="M0 5 V0 H5" />
		</svg>
	);
}

function MoleculeGlyph({ className = "" }: { className?: string }) {
	return (
		<svg
			aria-hidden
			viewBox="0 0 140 140"
			className={className}
			fill="none"
			stroke="currentColor"
			strokeWidth="1"
		>
			<circle cx="70" cy="22" r="6" />
			<circle cx="22" cy="60" r="6" />
			<circle cx="118" cy="60" r="6" />
			<circle cx="48" cy="110" r="6" />
			<circle cx="92" cy="110" r="6" />
			<circle cx="70" cy="70" r="6" fill="currentColor" fillOpacity="0.35" />
			<path d="M70 28 L70 64 M70 76 L70 104 M28 60 L64 67 M76 67 L112 60 M52 105 L66 76 M88 105 L74 76" />
		</svg>
	);
}

function Spec({
	label,
	value,
	mono,
	wrap,
}: {
	label: string;
	value: string;
	mono?: boolean;
	wrap?: boolean;
}) {
	return (
		<div className="min-w-0">
			<dt className="font-mono text-[0.55rem] uppercase tracking-[0.16em] text-muted-foreground">{label}</dt>
			<dd
				className={`mt-0.5 ${wrap ? "whitespace-normal break-words leading-snug" : "truncate"} ${mono ? "font-mono" : ""} text-foreground`}
			>
				{value}
			</dd>
		</div>
	);
}

export function HeroCompoundView({
	peptide,
	imageSrc,
	href,
	featuredLabel,
	sequenceLabel = "Seq.",
	originLabel = "Origin",
}: HeroCompoundViewProps) {
	const originShort = peptide.origin.split(" · ")[1] ?? peptide.origin;

	const card = (
		<div className="relative aspect-square w-full overflow-hidden rounded-2xl border border-border bg-card">
			<div
				aria-hidden
				className="absolute inset-0 opacity-[0.07]"
				style={{
					backgroundImage:
						"linear-gradient(to right, var(--primary) 1px, transparent 1px), linear-gradient(to bottom, var(--primary) 1px, transparent 1px)",
					backgroundSize: "40px 40px",
				}}
			/>
			<Corner className="left-3 top-3" />
			<Corner className="right-3 top-3 rotate-90" />
			<Corner className="bottom-3 left-3 -rotate-90" />
			<Corner className="bottom-3 right-3 rotate-180" />

			<div className="absolute inset-x-0 top-0 flex items-start justify-between px-3 pt-2.5 font-mono text-[0.48rem] uppercase tracking-[0.16em] text-muted-foreground sm:text-[0.54rem] lg:p-5 lg:text-[0.62rem] lg:tracking-[0.2em]">
				<span className="flex items-center gap-1.5 text-primary lg:gap-2">
					<span className="block h-1 w-1 rounded-full bg-primary lg:h-1.5 lg:w-1.5" />
					{peptide.code}
				</span>
				{featuredLabel ? (
					<span className="hidden sm:inline">{featuredLabel}</span>
				) : (
					<span className="sm:hidden">&nbsp;</span>
				)}
			</div>

			{imageSrc ? (
				<div className="absolute inset-1 bottom-[3.8rem] overflow-hidden rounded-lg bg-white sm:inset-2 sm:bottom-[5rem] lg:inset-3 lg:bottom-[6.5rem] lg:rounded-xl">
					<Image
						src={imageSrc}
						alt={`${peptide.code} · ${peptide.classification}`}
						fill
						sizes="(min-width: 1024px) 500px, 50vw"
						className="object-contain"
					/>
				</div>
			) : (
				<div className="absolute inset-0 bottom-[3.5rem] flex items-center justify-center lg:bottom-[5rem]">
					<MoleculeGlyph className="text-primary/35 h-16 w-16 lg:h-32 lg:w-32" />
				</div>
			)}

			<div className="border-border/60 bg-card/85 absolute inset-x-0 bottom-0 border-t px-3 py-2.5 backdrop-blur-sm lg:p-5">
				<div className="truncate font-mono text-[0.48rem] uppercase tracking-[0.14em] text-muted-foreground sm:text-[0.54rem] lg:text-[0.6rem] lg:tracking-[0.18em]">
					{peptide.classification}
				</div>
				<div className="mt-0.5 truncate font-display text-[0.88rem] font-medium tracking-[-0.005em] sm:text-[0.98rem] lg:mt-1 lg:text-[1.15rem] lg:tracking-[-0.01em]">
					{peptide.name}
				</div>
				<dl className="mt-3 hidden grid-cols-3 gap-3 text-[0.7rem] lg:grid">
					{peptide.mw && (
						<Spec
							label="MW"
							value={peptide.mw}
							mono
							wrap={peptide.mw.includes(" · ") || peptide.mw.includes("(")}
						/>
					)}
					{peptide.cas ? (
						<Spec label="CAS" value={peptide.cas} mono />
					) : (
						<Spec label={sequenceLabel} value={peptide.sequence ?? "—"} mono />
					)}
					<Spec label={originLabel} value={originShort} />
				</dl>
			</div>
		</div>
	);

	if (href) {
		return (
			<Link href={href} className="block transition-opacity hover:opacity-95">
				{card}
			</Link>
		);
	}

	return card;
}
